<script setup lang="ts">
import { computed, type HTMLAttributes, provide, ref, useId, watch } from 'vue';
import NxTablist from './NxTablist.vue';
import { type NxTabEntry, tabsInjectionKey } from './internal';

const props = defineProps<
  {
    /** The unique identifier of the selected `NxTabpanel` within this component. Defaults to the first tab */
    selectedTabId?: string;
    /** The label text for the `NxTablist` */
    tablistAriaLabel?: string;
    /**
     * Smaller tabs that keep one line and scroll sideways when they do not fit —
     * for narrow side panels. Defaults to `false`.
     */
    compact?: boolean;
    /** Wrap tabs onto additional rows instead of scrolling sideways. */
    wrap?: boolean;
    /**
     * The tab row sticks to the top of a scrolling container (a long form in a
     * modal) on the surface background. Defaults to `false`.
     */
    sticky?: boolean;
    /**
     * What the sticky tab row paints behind itself, so scrolled content does not show through:
     * `'surface'` (a card or modal body) or `'page'` (tabs placed directly on the page
     * background). Only used with `sticky`. Defaults to `'surface'`.
     */
    stickyBackground?: 'surface' | 'page';
    /**
     * The panels start a standard gap below the tab row (`--nxt-space-md`) — for a page or
     * card whose first content is a field or a toolbar. Defaults to `false`.
     */
    spaced?: boolean;
    /**
     * Fill the height of a flex-column parent (a modal body with `fill`): the
     * tablist stays in place, the active panel takes the rest of the height and
     * scrolls itself, set off from the tablist by the kit's spacing. Defaults
     * to `false`.
     */
    fill?: boolean;
    /**
     * The panels do not scroll themselves: their content takes its natural height and the
     * surrounding container (a page or a modal body) scrolls as a whole. Switch it on where the
     * container, not the panel, owns the scroll — for example `fill` on a wide page and `flow` on
     * a narrow one. Defaults to `false`.
     */
    flow?: boolean;
  } & /* @vue-ignore */ HTMLAttributes
>();

/**
 * `update:selectedTabId` makes the prop `v-model`-compatible. It fires both on user-driven
 * tab changes (via `setSelectedTab`) and on the implicit initial selection that happens when
 * the first tab registers itself with no `selectedTabId` prop set — so an outside observer
 * (e.g. `NavContext`) learns about the initial tab without having to compute it itself.
 */
const emit = defineEmits<{
  'update:selectedTabId': [tabId: string];
}>();

const tabs = ref(new Map<string, NxTabEntry>());
const compact = computed(() => props.compact === true);
const wrap = computed(() => props.wrap === true);
const sticky = computed(() => props.sticky === true);
const spaced = computed(() => props.spaced === true);
const fill = computed(() => props.fill === true);
const flow = computed(() => props.flow === true);
const stickyBackground = computed(() => (props.stickyBackground === 'page' ? 'page' : 'surface'));
const removeTab = (tabId: string) => {
  tabs.value.delete(tabId);
};
const selectedTabId = ref(props.selectedTabId);
const setSelectedTab = (tabId: string) => {
  if (selectedTabId.value === tabId) return;
  selectedTabId.value = tabId;
  emit('update:selectedTabId', tabId);
};
// Keep the local selection in sync if the parent drives it via `v-model:selectedTabId`.
watch(
  () => props.selectedTabId,
  (next) => {
    if (next !== undefined && next !== selectedTabId.value) selectedTabId.value = next;
  }
);
const addTab = (tabId: string, tabLabel: string, count?: number) => {
  tabs.value.set(tabId, { label: tabLabel, count });

  if (selectedTabId.value === undefined) setSelectedTab(tabId);

  return () => {
    removeTab(tabId);
  };
};

const updateTab = (tabId: string, tabLabel: string, count?: number) => {
  if (!tabs.value.has(tabId)) return;
  tabs.value.set(tabId, { label: tabLabel, count });
};

const tabsId = useId();
const getTabElementId = <TabId extends string>(tabId: TabId) => {
  return `tab_${tabsId}_${tabId}` as const;
};
const getTabpanelElementId = <TabId extends string>(tabId: TabId) => {
  return `panel_${tabsId}_${tabId}` as const;
};

provide(tabsInjectionKey, {
  tabs,
  compact,
  wrap,
  sticky,
  spaced,
  fill,
  flow,
  stickyBackground,
  selectedTabId,
  setSelectedTab,
  addTab,
  updateTab,
  getTabElementId,
  getTabpanelElementId,
});
</script>
<template>
  <div class="tabs" :class="{ 'is-fill': fill }">
    <NxTablist :aria-label="$props.tablistAriaLabel" />

    <!-- This includes the tabpanels that populate the tablist using the addTab function -->
    <slot></slot>
  </div>
</template>
<style scoped>
.tabs {
  display: flex;
  flex-direction: column;
  /* Allow the component to shrink within a constrained-height flex parent so the panel can scroll
     internally instead of overflowing the page; in an unconstrained parent the panel sizes to
     content. */
  min-height: 0;
  min-width: 0;
  height: 100%;
  width: 100%;
}

/* `fill`: a flex item that takes the parent's remaining height, whatever sits
   beside it, instead of claiming 100 % of it. */
.tabs.is-fill {
  flex: 1 1 auto;
  height: auto;
}
</style>
